import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import styles from './EmailGating.module.scss';
import { ContinueWithGoogle } from '../ContinueWithGoogle';
import { Form } from 'react-bootstrap';
import { Formik } from 'formik';
import { BigButton } from '../../common';
import * as Yup from 'yup';
import {
  emailIsValid,
  emailIsValidWithExtension,
  emailRegExp,
  getLocalStorage,
} from '../../helpers/jsHelper';
import { errorToast, successToast } from '../../helpers/toastHelper';
import { CURRENT_USER, EG_SUBMISSION } from '../../constants/projectConstants';
import { apiPostProductPageViewed } from '../../helpers/api';
import { getAndSetCookieData } from '../../helpers/apiHelper';
import { useParams } from 'react-router-dom';
import { Mixpanel, MxEvent, RESTRICTED_EMAIL_DOMAIN } from '../../common/Mixpanel';

const shouldTrackUser = (userEmail = null) => {
  const getLoggedinUserEmail = () => {
    if (userEmail) return userEmail;

    return localStorage.getItem(CURRENT_USER)
      ? JSON.parse(localStorage.getItem(CURRENT_USER))?.user?.email
      : null;
  };

  const trackAllowedEmailDomains = !RESTRICTED_EMAIL_DOMAIN.some((domain) =>
    getLoggedinUserEmail()?.includes(domain)
  );
  return trackAllowedEmailDomains;
};

const getValidEmail = (_email) => {
  const email = _email?.trim();
  return email && emailIsValid(email) && emailIsValidWithExtension(email) ? email : null;
};

export const shouldTrackPageViewed = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('t') === '1';
};

export const sendProductPageViewedData = async ({ email = null, productUUID = null }) => {
  const loggedInUser = await getLocalStorage(CURRENT_USER, true);
  const _email =
    email || loggedInUser?.user?.email || getValidEmail(localStorage.getItem(EG_SUBMISSION));

  // preventing api call for email submitted by internal user if loggedin or when submitting any restricted domain email unless the debugging mode is enabled
  if (!shouldTrackUser(_email)) {
    return { status: true };
  }

  if (!_email || !productUUID) return;

  // sending analytics data
  const clarityIdentifyResponse = await window.clarity('identify', _email);
  window.clarity('set', 'email', [_email]);

  // sending product page & analytics response data
  const response = await apiPostProductPageViewed({
    data: {
      email: _email,
      page_name: window.document.title?.replace(' - imagine.io', ''),
      page_url: window.location.href,
      ...(clarityIdentifyResponse || {}),
    },
  });

  if (response?.status) {
    getAndSetCookieData({
      cookieName: `ppv_${productUUID}`, // ppv - product page viewed
      cookieData: _email,
      cookieExpireTime: 3600000, // one hours
    });
  }

  return response;
};

export const EmailGating = ({
  isPublicPage = false,
  hasCategory = false,
  isLoggedIn = false,
  containerClassName = '',
  containerStyle = {},
  eventProperties = {},
  addUserEngagementToApi = () => {}
}) => {
  const [show, setShow] = useState(false);

  // Function to determine if the email prompt needs to be shown
  const checkIfEmailNeedsToAsk = async () => {
    const user = await getLocalStorage(CURRENT_USER, true);

    if (!isPublicPage) return; // Do not show if not a public page

    if (user || isLoggedIn) {
      setShow(false); // If logged in, do not show the prompt
      document.body.style.overflow = '';
    } else {
      const email = localStorage.getItem(EG_SUBMISSION); // Email Gating Submission
      const _show = getValidEmail(email);
      setShow(!_show); // Show prompt if email is not valid
      document.body.style.overflow = !_show ? 'hidden' : '';
    }
  };

  // Effect to check email status and set up storage event listener
  useEffect(() => {
    if (!shouldTrackPageViewed() || isLoggedIn) return; // Skip if user is logged in
    checkIfEmailNeedsToAsk();
    window.addEventListener('storage', checkIfEmailNeedsToAsk);
    return () => window.removeEventListener('storage', checkIfEmailNeedsToAsk);
  }, [isPublicPage, isLoggedIn]);

  if (!show) return null;

  return (
    <div
      className={cx(styles.wrapper, hasCategory ? styles.hasCategory : '', containerClassName)}
      style={containerStyle}>
      <div className={styles.container}>
        <h2 className={styles.title}>Unlock Full Product Experience</h2>
        <p className={styles.description}>
          Enter your email address to see all product images, variants, and details
        </p>
        <EmailForm
          onSuccess={(email) => {
            checkIfEmailNeedsToAsk();
            addUserEngagementToApi(email)
            // identify user as the email is setting on the platform
            // this will work for the non-loggedin user & on public product page only
            Mixpanel.identify();

            Mixpanel.track(true, MxEvent.EMAIL_SUBMITTED, {
              email,
              ...eventProperties,
            });
            setTimeout(
              () => Mixpanel.track(true, MxEvent.PAGE_VIEWED, { email, ...eventProperties }),
              1000
            );
          }}
        />

        {/* <div className="separator mb-4">
          <small className="fw-normal">OR</small>
        </div>
        <ContinueWithGoogle
          text="Continue with Google"
          onClick={() => {}}
          containerStyle={{
            borderRadius: 5,
          }}
          buttonStyle={{
            height: 48,
          }}
        /> */}
      </div>
    </div>
  );
};

const EmailForm = ({ onSuccess }) => {
  const routeParams = useParams();
  const productUUID = routeParams.id;

  const initialValues = {
    email: '',
  };

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email')
      .matches(emailRegExp, 'Use a business email only')
      .required('Email is required'),
  });

  const onSubmit = async (values) => {
    const email = getValidEmail(values?.email);
    if (email) {
      try {
        const response = await sendProductPageViewedData({
          email,
          productUUID,
        });
        if (response?.status) {
          localStorage.setItem(EG_SUBMISSION, email);
          successToast('Thank you for submitting your email.');
          onSuccess?.(email);
        } else {
          errorToast('Unable to submit the email. Please try again later.');
        }
      } catch (error) {
        errorToast('Unable to submit the email. Please try again later.');
      }
    }
  };

  const settings = {
    initialValues,
    validationSchema,
    onSubmit,
  };

  return (
    <Formik {...settings}>
      {({
        values,
        errors,
        touched,
        isSubmitting,
        handleChange,
        handleBlur,
        handleSubmit,
        setTouched,
      }) => (
        <Form
          className={styles.form}
          onKeyDown={(e) => {
            const code = e?.keyCode || e?.which;
            if (code === 13) {
              e.preventDefault();
              handleSubmit();
            }
          }}>
          <Form.Group className="form-outline">
            {errors.email && touched.email ? (
              <Form.Label className="text-danger">
                <span>{errors.email}</span>
              </Form.Label>
            ) : (
              <Form.Label className="opacity-0">
                <span>Email</span>
              </Form.Label>
            )}
            <Form.Control
              type="text"
              name="email"
              className={cx(errors.email && touched.email && 'input-error')}
              onChange={handleChange}
              onFocus={() => setTouched({ ...touched, email: false })}
              onBlur={handleBlur}
              placeholder="Email address"
              value={values.email}
            />
          </Form.Group>

          <Form.Group className="form-btn">
            <BigButton
              type="button"
              disabled={isSubmitting}
              className="bigButton lf-btn-1 w-100"
              buttonTitle="Continue"
              onClick={handleSubmit}
              isLoading={isSubmitting}
            />
          </Form.Group>
        </Form>
      )}
    </Formik>
  );
};
